
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Edit, Plus, Trash, Code } from 'lucide-react';
import { CommandShortcut, getShortcutCommands, updateShortcutCommand, deleteShortcutCommand } from '@/services/admin/shortcutCommandService';
import { toast } from 'sonner';

const ShortcutsManager = () => {
  const [shortcuts, setShortcuts] = useState<CommandShortcut[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingShortcut, setEditingShortcut] = useState<CommandShortcut | null>(null);
  const [formValues, setFormValues] = useState<Partial<CommandShortcut>>({
    code: '',
    name: '',
    description: '',
    entity_type: 'task',
    action_type: 'date'
  });
  const [parametersJson, setParametersJson] = useState('{}');
  const [activeTab, setActiveTab] = useState('all');

  // Load shortcuts on mount
  useEffect(() => {
    loadShortcuts();
  }, []);

  // Load shortcuts from the database
  const loadShortcuts = async () => {
    setLoading(true);
    const data = await getShortcutCommands();
    setShortcuts(data);
    setLoading(false);
  };

  // Set up form for editing a shortcut
  const handleEditShortcut = (shortcut: CommandShortcut) => {
    setEditingShortcut(shortcut);
    setFormValues({
      code: shortcut.code,
      name: shortcut.name,
      description: shortcut.description || '',
      entity_type: shortcut.entity_type,
      action_type: shortcut.action_type
    });
    setParametersJson(JSON.stringify(shortcut.parameters || {}, null, 2));
  };

  // Set up form for creating a new shortcut
  const handleAddNewShortcut = () => {
    setEditingShortcut(null);
    setFormValues({
      code: '',
      name: '',
      description: '',
      entity_type: 'task',
      action_type: 'date'
    });
    setParametersJson('{\n  "pattern": "",\n  "example": ""\n}');
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let parameters: any;
    try {
      parameters = JSON.parse(parametersJson);
    } catch (err) {
      toast.error('Invalid JSON in parameters field');
      return;
    }
    
    const shortcutData: Partial<CommandShortcut> & { id?: number } = {
      ...formValues,
      parameters
    };
    
    if (editingShortcut) {
      shortcutData.id = editingShortcut.id;
    }
    
    const success = await updateShortcutCommand(shortcutData);
    
    if (success) {
      toast.success(editingShortcut ? 'Shortcut updated successfully' : 'New shortcut created successfully');
      await loadShortcuts();
      setEditingShortcut(null);
      handleAddNewShortcut(); // Reset the form
    }
  };

  // Handle deleting a shortcut
  const handleDeleteShortcut = async (id: number) => {
    const success = await deleteShortcutCommand(id);
    if (success) {
      toast.success('Shortcut deleted successfully');
      await loadShortcuts();
    }
  };

  // Filter shortcuts based on active tab
  const filteredShortcuts = shortcuts.filter(s => 
    activeTab === 'all' || s.entity_type === activeTab
  );

  return (
    <div className="container mx-auto py-6 px-4">
      <Helmet>
        <title>Shortcuts Manager | CRM</title>
      </Helmet>
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Shortcuts Manager</h1>
        <p className="text-muted-foreground mt-2">
          Create and manage shortcut commands for tasks and contacts
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Shortcut form */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>{editingShortcut ? 'Edit Shortcut' : 'Add New Shortcut'}</CardTitle>
            <CardDescription>
              {editingShortcut 
                ? `Editing shortcut: ${editingShortcut.name}`
                : 'Create a new shortcut command'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Command Code</Label>
                <Input 
                  id="code" 
                  placeholder="e.g., p: or \d+"
                  value={formValues.code}
                  onChange={(e) => setFormValues({...formValues, code: e.target.value})}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  The command pattern to match (can use regex patterns)
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="name">Display Name</Label>
                <Input 
                  id="name" 
                  placeholder="e.g., Set Priority"
                  value={formValues.name}
                  onChange={(e) => setFormValues({...formValues, name: e.target.value})}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description" 
                  placeholder="Explain what this command does"
                  value={formValues.description}
                  onChange={(e) => setFormValues({...formValues, description: e.target.value})}
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="entity_type">Entity Type</Label>
                  <Select 
                    value={formValues.entity_type}
                    onValueChange={(value) => setFormValues({...formValues, entity_type: value as 'task' | 'contact' | 'all'})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select entity type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="task">Task</SelectItem>
                      <SelectItem value="contact">Contact</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="action_type">Action Type</Label>
                  <Select 
                    value={formValues.action_type}
                    onValueChange={(value) => setFormValues({...formValues, action_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select action type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="priority">Priority</SelectItem>
                      <SelectItem value="stage">Stage</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="assignment">Assignment</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="parameters">Parameters (JSON)</Label>
                <Textarea 
                  id="parameters" 
                  value={parametersJson}
                  onChange={(e) => setParametersJson(e.target.value)}
                  rows={8}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">
                  JSON parameters for pattern matching and command execution
                </p>
              </div>
              
              <div className="flex space-x-2">
                <Button type="submit">
                  {editingShortcut ? 'Update Shortcut' : 'Create Shortcut'}
                </Button>
                {editingShortcut && (
                  <Button type="button" variant="outline" onClick={handleAddNewShortcut}>
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
        
        {/* Right column: Shortcuts list */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Registered Shortcuts</CardTitle>
              <Button size="sm" variant="outline" onClick={() => loadShortcuts()}>
                Refresh
              </Button>
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="task">Tasks</TabsTrigger>
                <TabsTrigger value="contact">Contacts</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p>Loading shortcuts...</p>
            ) : filteredShortcuts.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No shortcuts found</p>
                <Button onClick={handleAddNewShortcut} variant="secondary">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Shortcut
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredShortcuts.map((shortcut) => (
                    <TableRow key={shortcut.id}>
                      <TableCell>
                        <code className="bg-muted px-1 py-0.5 rounded text-sm">
                          {shortcut.code}
                        </code>
                      </TableCell>
                      <TableCell>
                        {shortcut.name}
                        {shortcut.description && (
                          <p className="text-xs text-muted-foreground mt-1">{shortcut.description}</p>
                        )}
                      </TableCell>
                      <TableCell>{shortcut.action_type}</TableCell>
                      <TableCell>{shortcut.entity_type}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => handleEditShortcut(shortcut)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="outline">
                                <Trash className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Shortcut</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete the shortcut "{shortcut.name}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteShortcut(shortcut.id)}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
      
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Using Shortcuts</CardTitle>
          <CardDescription>
            Reference guide for using shortcut commands
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 border rounded-md">
              <h3 className="font-semibold flex items-center mb-2">
                <Code className="h-4 w-4 mr-2" />
                Basic Commands
              </h3>
              <ul className="space-y-2 text-sm">
                <li><code className="bg-muted px-1.5 py-0.5 rounded">5</code> - Sets next action date to 5 days from today</li>
                <li><code className="bg-muted px-1.5 py-0.5 rounded">d:14</code> - Sets next action date to 14 days from today</li>
                <li><code className="bg-muted px-1.5 py-0.5 rounded">p:high</code> - Sets priority to High</li>
                <li><code className="bg-muted px-1.5 py-0.5 rounded">s:active</code> - Sets stage to Active</li>
              </ul>
            </div>
            
            <div className="p-4 border rounded-md">
              <h3 className="font-semibold flex items-center mb-2">
                <Code className="h-4 w-4 mr-2" />
                Combined Commands
              </h3>
              <ul className="space-y-2 text-sm">
                <li><code className="bg-muted px-1.5 py-0.5 rounded">5 p:high</code> - Sets date to 5 days out and priority to High</li>
                <li><code className="bg-muted px-1.5 py-0.5 rounded">p:medium s:next</code> - Sets priority Medium and stage to Next</li>
              </ul>
            </div>
            
            <div className="p-4 border rounded-md">
              <h3 className="font-semibold flex items-center mb-2">
                <Code className="h-4 w-4 mr-2" />
                Creating New Commands
              </h3>
              <p className="text-sm">
                Use the form to create custom commands with regex patterns. 
                Advanced users can define complex patterns and behaviors.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShortcutsManager;
